import React, { useEffect, useState } from 'react';
import useReferenceData from './components/hooks/useReferenceData';
import axios from 'axios';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import usePagination10 from './components/hooks/usePagination10';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import SaldoContaModal from './components/modals/SaldoContaModal';
import { API_BASE_URL } from '../config/ApiConfig';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

const emptyForm = {
  ano: '', idPrograma: '', idEscola: '', idConta: '', saldoFinal: ''
};

function keyOf(item) {
  return `${item.ano}_${item.idPrograma}_${item.idEscola}_${item.idConta}`;
}

function formatCurrency(value) {
  if (value == null) return '';
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function SaldosConta() {
      const token = localStorage.getItem('token');
      const cnpj = localStorage.getItem('cnpj');
      const ano = localStorage.getItem('ano');
      // Referências de programas e contas
      const { programas, contas } = useReferenceData(token, cnpj);
    async function handleImportJson(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async evt => {
        try {
          const json = JSON.parse(evt.target.result);
          if (!json.saldos || !Array.isArray(json.saldos)) throw new Error('JSON inválido: campo "saldos" ausente.');
          // Para cada saldo, buscar o idPrograma da conta
          const saldosWithPrograma = await Promise.all(json.saldos.map(async saldo => {
            // Buscar conta para obter idPrograma
            const contaResp = await axios.get(`${API_BASE_URL.CONTA}/findByEscola`, {
              params: { idEscola: cnpj },
              headers: { Authorization: token ? `Bearer ${token}` : undefined }
            });
            const contas = Array.isArray(contaResp.data) ? contaResp.data : [];
            const contaObj = contas.find(c => c.numConta === saldo.conta);
            if (!contaObj) throw new Error(`Conta não encontrada: ${saldo.conta}`);
            return {
              idEscola: cnpj,
              ano: ano,
              idPrograma: contaObj.idPrograma,
              idConta: saldo.conta,
              saldoFinal: saldo.saldo
            };
          }));
          // Enviar todos os saldos
          await Promise.all(saldosWithPrograma.map(payload =>
            axios.post(`${API_BASE_URL.SALDO_CONTA}`, payload, {
              headers: { Authorization: token ? `Bearer ${token}` : undefined }
            })
          ));
          setAtualizar(a => !a);
          showDefaultModal('success', 'Importação concluída!');
        } catch (err) {
          showDefaultModal('error', 'Erro ao importar: ' + err.message);
        }
      };
      reader.readAsText(file);
      e.target.value = '';
    }
  // ...token, cnpj, ano já definidos acima
  const [items, setItems] = useState([]);
  const [sortField, setSortField] = useState('idConta');
  const [sortDir, setSortDir] = useState('asc');
  const [form, setForm] = useState({ ...emptyForm, idEscola: cnpj || '', ano: ano || '' });
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);

  function getContaSortKey(item) {
    const c = (contas || []).find(ct => String(ct.idConta) === String(item.idConta) || String(ct.numConta) === String(item.idConta));
    const raw = c ? (c.numConta || c.idConta || '') : (item._contaLabel || item.idConta || '');
    const s = String(raw).trim();
    const m = s.match(/^(\d+)/);
    const num = m ? parseInt(m[1], 10) : NaN;
    const sufMatch = s.match(/-(.*)$/);
    const suffix = sufMatch ? sufMatch[1].toString().toUpperCase() : '';
    return { raw: s, num, suffix };
  }

  const sortedItems = React.useMemo(() => {
    if (!sortField) return items;
    const copy = [...items];
    copy.sort((a, b) => {
      if (sortField === 'idConta') {
        const ka = getContaSortKey(a);
        const kb = getContaSortKey(b);
        if (!isNaN(ka.num) && !isNaN(kb.num) && ka.num !== kb.num) {
          return (ka.num - kb.num) * (sortDir === 'asc' ? 1 : -1);
        }
        if (ka.num === kb.num) {
          const s = ka.suffix.localeCompare(kb.suffix, 'pt-BR', { sensitivity: 'base' });
          if (s !== 0) return s * (sortDir === 'asc' ? 1 : -1);
        }
        return ka.raw.localeCompare(kb.raw, 'pt-BR', { sensitivity: 'base' }) * (sortDir === 'asc' ? 1 : -1);
      }
      let va = a[sortField] == null ? '' : String(a[sortField]);
      let vb = b[sortField] == null ? '' : String(b[sortField]);
      return va.localeCompare(vb, 'pt-BR', { sensitivity: 'base' }) * (sortDir === 'asc' ? 1 : -1);
    });
    return copy;
  }, [items, sortField, sortDir, contas]);

  const displayed = sortedItems.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  const resumoPorPrograma = React.useMemo(() => {
    const map = {};
    (Array.isArray(items) ? items : []).forEach(it => {
      const chave = String(it.idPrograma);
      if (!map[chave]) map[chave] = { idPrograma: it.idPrograma, total: 0, qtd: 0 };
      map[chave].total += parseFloat(it.saldoFinal) || 0;
      map[chave].qtd += 1;
    });
    return Object.values(map);
  }, [items]);

  useEffect(() => {
    setIsLoading(true);
    axios.get(`${API_BASE_URL.SALDO_CONTA}`, {
      params: { idEscola: cnpj, ano: ano },
      headers: { Authorization: token ? `Bearer ${token}` : undefined }
    })
      .then(r => setItems(r.data))
      .catch(() => showDefaultModal('error', 'Erro ao carregar saldos de conta.'))
      .finally(() => setIsLoading(false));
  }, [atualizar]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }
  function handleNew() {
    setForm({ ...emptyForm, idEscola: cnpj || '', ano: ano || '', _isEditing: false });
    setShowModal(true);
  }
  function handleEdit() {
    if (selected.length !== 1) return;
    const s = selected[0];
    setForm({ ...s, _isEditing: true });
    setShowModal(true);
  }
  function handleSave(e) {
    e.preventDefault();
    axios.post(`${API_BASE_URL.SALDO_CONTA}`, form, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined }
    })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]);
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar saldo de conta.'));
  }
  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s =>
      axios.delete(`${API_BASE_URL.SALDO_CONTA}?ano=${s.ano}&idPrograma=${s.idPrograma}&idEscola=${s.idEscola}&idConta=${s.idConta}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined }
      })
    ))
      .then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir saldo de conta.'))
    );
  }
  function toggleSelect(item) {
    setSelected(prev => prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]);
  }

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="SALDO CONTA" icon="bi-bank2" />
        <PageContent>
          <Toolbar onNew={handleNew} onEdit={handleEdit} disableEdit={selected.length !== 1} onDelete={handleDelete} disableDelete={selected.length === 0}>
            <label className="btn btn-info btn-sm botao-igual mb-0">
              <i className="bi bi-upload me-1"></i>Importar JSON
              <input type="file" accept="application/json" style={{ display: 'none' }} onChange={handleImportJson} />
            </label>
          </Toolbar>
          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => {
                const programa = (programas || []).find(p => String(p.id) === String(prog.idPrograma));
                return (
                  <div className="card border-0 shadow-sm" key={prog.idPrograma} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 200, maxWidth: '25%' }}>
                    <div className="card-body pt-2 pb-1 px-3">
                      <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                        <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={prog.idPrograma}>{programa ? programa.descricao : prog.idPrograma}</span>
                        <span className="badge text-bg-light text-success flex-shrink-0" style={{ fontSize: '.62rem' }}>{prog.qtd}</span>
                      </div>
                      <div className="d-flex justify-content-between fw-bold" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                        <span>Total</span><span className="text-success">{formatCurrency(prog.total)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <TableContainer isLoading={false}>
              <DataTable head={
                  <tr>
                    <th>#</th>
                    <th>Ano</th>
                    <th>Programa</th>
                    <th>Conta</th>
                    <th className='text-end'>Saldo Final</th>
                  </tr>
                }>
                <tbody>
                  {displayed.map((item, idx) => {
                    const programa = (programas || []).find(p => p.id === item.idPrograma);
                    return (
                      <tr key={keyOf(item)} className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                        <td>{setPagination.startIndex + idx + 1}</td>
                        <td>{item.ano}</td>
                        <td>{programa ? programa.descricao : item.idPrograma}</td>
                        <td>{item.idConta}</td>
                        <td className="text-end">{formatCurrency(item.saldoFinal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <SaldoContaModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSave}
        form={form}
        handleChange={handleChange}
      />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}
